/**
 * Reviews-area breadcrumb origins. Built on the generic `from`-origin helpers in
 * `breadcrumbOrigin.ts`; this module only owns the review-specific matcher table.
 *
 * The interior review pages — the review detail page (`/view-review/:id`) and the
 * single form viewer (`/review/:id`) — root their breadcrumb on the page the user
 * came from, carried in the `from` query param.
 */
import { BREADCRUMB_FROM_PARAM, type OriginMatcher as BaseOriginMatcher, resolveOrigin } from "./breadcrumbOrigin";

export { appendCurrentLocationAsFrom, getSanitizedFrom } from "./breadcrumbOrigin";

export const REVIEWS_BREADCRUMB_FROM_PARAM = BREADCRUMB_FROM_PARAM;

// Marks a form link that was reached by drilling through the review detail page, so the form
// keeps its parent-review crumb even for "direct" origins (e.g. the Dashboard Reviews widget).
export const REVIEW_VIA_REVIEW_PARAM = "viaReview";

export const parseViaReview = (search: string): boolean =>
	new URLSearchParams(search).get(REVIEW_VIA_REVIEW_PARAM) === "true";

export const REVIEW_CYCLES_LIST_HREF = "/company/reviews/cycles/?name=start_date&direction=desc";

export type ReviewsOriginKind = "direct" | "review" | "cycle";

type OriginMatcher = BaseOriginMatcher & {
	kind: ReviewsOriginKind;
};

// Order matters: more specific paths first (e.g. reviews-of-my-team before reviews-of-me).
const ORIGIN_MATCHERS: OriginMatcher[] = [
	{ match: "/forms-i-need-to-do", label: "Forms I Need To Do", kind: "direct" },
	{ match: "/forms-i-did", label: "Forms I Did", kind: "direct" },
	{ match: "/reviews-of-my-team", label: "My Team's Reviews", kind: "review" },
	{ match: "/reviews-of-me", label: "Reviews of Me", kind: "review" },
	{ match: "/company/reviews/all", label: "Company Reviews", kind: "review" },
	{ match: "/company/reviews/form-report", label: "Company Forms", kind: "direct" },
	{ match: "/review-cycle/", label: "Review Cycles", kind: "cycle" },
	{ match: "/company/reviews/cycles", label: "Review Cycles", kind: "cycle" },
];

export type ReviewsOrigin = {
	/** Sanitized internal `from` path (empty string when absent or invalid). */
	from: string;
	/** Label for the originating page, or null when there is no recognized origin. */
	label: string | null;
	/** Breadcrumb structure to render, or null when there is no recognized origin. */
	kind: ReviewsOriginKind | null;
};

// The dashboard renders at "/", "/user/:id", and "/employee-page/:id" — all of which mean
// "Dashboard". "/" can't be a substring matcher (it matches everything), so detect it here.
const isDashboardFrom = (from: string): boolean =>
	from === "/" || from.startsWith("/user/") || from.startsWith("/employee-page");

export const parseReviewsOrigin = (search: string): ReviewsOrigin => {
	const { from, matcher } = resolveOrigin(search, ORIGIN_MATCHERS);
	if (matcher) return { from, label: matcher.label, kind: matcher.kind };
	if (from && isDashboardFrom(from)) return { from, label: "Dashboard", kind: "direct" };
	return { from, label: null, kind: null };
};
